'use client'

import _ from 'lodash'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { marked } from 'marked'
import { useState, useEffect } from 'react'
import { useParams } from 'next/navigation'
import NextLink from 'next/link'
import { MenuItem } from '@headlessui/react'
import { useQuery } from '@tanstack/react-query'
import { TrashIcon, ArrowUturnLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline'

import { spottedApi, myCruzApi } from '@/api'
import { Api, Spotted } from '@cruz-com/lookup'
import {
  Alert, Bullet, Button, Confirm, H1, LinkButton, NavUserSide, NavUserTop, NavMy, Error, DropdownMenu,
  Like, Views
} from '@/components'
import { useTrackingView } from '@/lib'
import { useGlobalStore } from '@/store/provider'
import { Photos } from './_photos'
import { pages } from '@/settings'

type Props = {
  initialData?: Api.Result<Spotted.Detail>
}

dayjs.extend(relativeTime)

export function SpottedDetail({ initialData }: Props) {
  const { user, setToast } = useGlobalStore((state) => state)
  const [spotted, setSpotted] = useState<Spotted.Detail | any>(initialData?.data || {})
  const [showConfirm, setShowConfirm] = useState(false)
  const { id }: { id: string } = useParams()
  const { viewCount } = useTrackingView({ name: 'spotted', id }, 0)

  const { data, error, refetch } = useQuery({
    queryKey: [`SpottedDetail_${id}`, id],
    queryFn: () => spottedApi.getDetail(id),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000,
    initialData,
    retry: false
  })

  useEffect(() => {
    if (data?.data) {
      setSpotted(data.data)
    }
  }, [data?.data])

  // confirm & delete
  async function deleteSpotted() {
    setShowConfirm(false)
    try {
      await myCruzApi.spotted.deleteSpotted(id)
      refetch()
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message })
    }
  }

  // undelete
  async function undeleteSpotted() {
    try {
      await myCruzApi.spotted.undeleteSpotted(id)
      refetch()
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message })
    }
  }

  if (error) {
    return <Error error={error} />
  }

  if (_.isEmpty(spotted)) {
    return <></>
  }

  const MyMenu = () =>
    <>
      <div className='hidden lg:flex items-center gap-x-2 py-1 border-t lg:border-t-0'>
        <Button type='button' variant='outline' onClick={() => setShowConfirm(true)} className='px-6 h-8 rounded-full'>
          <TrashIcon className='size-4 -ml-1 mr-1.5' /> Delete
        </Button>
        <LinkButton variant='primary' href={`${pages.postSpotted.href}?id=${id}`} label='Edit' icon='edit' className='px-6 h-8 rounded-full' />
      </div>
      <div className='lg:hidden'>
        <DropdownMenu>
          <MenuItem>
            <NextLink hrefLang='en' href={`${pages.postSpotted.href}?id=${id}`} className='dropdown-menu-item'>
              <PencilSquareIcon className='size-4' />
              Edit
            </NextLink>
          </MenuItem>
          <MenuItem>
            <button type='button' onClick={() => setShowConfirm(true)} className='dropdown-menu-item'>
              <TrashIcon className='size-4 -ml-1 mr-1.5' /> Delete
            </button>
          </MenuItem>
        </DropdownMenu>
      </div>
    </>

  const { title, location_name, content, user_id, user_display, date_create, date_update, post_status_id, view_count, vote } = spotted || {}
  const isMine = (user?._id && user?._id === user_id)

  return (
    <div className='w-full'>
      {
        user?._id !== user_id &&
        <NavUserTop initialData={user_display || {}} position='top' />
      }
      <div className='max-w-[1536px] mx-auto lg:px-4 relative font-light flex flex-row gap-x-8 lg:divide-x dark:divide-zinc-700'>
        <Confirm
          title='Please Confirm'
          content='Are you sure you want to delete this?'
          open={showConfirm}
          onConfirm={() => { deleteSpotted() }}
          onCancel={() => { setShowConfirm(false) }}
        />

        <aside className='aside'>
          <nav className='side-nav'>
            {
              user?._id === user_id
                ? <NavMy />
                : <NavUserSide initialData={user_display || {}} />
            }
          </nav>
        </aside>

        <main className='flex-1 overflow-y-auto'>
          {
            (post_status_id! === -2) &&
            <Alert variant='error' className='mb-4 py-6 text-center'
              title='DELETED'
              message='This spotted has been deleted.'
              action={
                <Button type='button' variant='primary' className='mt-4 h-8 px-6 rounded-full' onClick={undeleteSpotted}>
                  <ArrowUturnLeftIcon className='size-5 -ml-1 mr-1.5' /> Undelete
                </Button>
              }
            />
          }
          <div className='px-4 lg:px-0'>
            <div className='w-full flex items-end justify-between gap-x-4'>
              <div>
                <H1>{title}</H1>
                <div className='-mt-2 flex flex-col md:flex-row text-sm font-light gap-2'>
                  <div className='flex items-center gap-x-2'>
                    {location_name}
                    <Bullet />
                    Posted {dayjs(date_create).fromNow()}
                  </div>
                  {
                    (isMine && dayjs(date_update).isAfter(dayjs(date_create))) &&
                    <div className='flex items-center gap-x-2'>
                      <Bullet className='hidden md:block' />
                      Updated {dayjs(date_update).fromNow()}
                    </div>
                  }
                </div>
              </div>
              {
                (isMine && post_status_id! > 0) && <MyMenu />
              }
            </div>
            {
              content &&
              <div className='pt-4 pb-2 font-normal' dangerouslySetInnerHTML={{ __html: marked.parse(content || '') }} />
            }
          </div>
          <Photos {...spotted} />
          <div className='flex items-center justify-center gap-4 pt-6'>
            <Views count={Math.max(view_count || 0, viewCount || 0)} />
            <Bullet />
            <Like name='spotted' id={id} user_id={user_id} my_user_id={user?._id} vote={vote || { up: {} }} onVoteChanged={refetch} />
          </div>
        </main>
      </div>
    </div>
  )
}
